/**
 * Where you stand against the brands you named (SYR-111 phase 3).
 *
 * Competitors are read at half the brand's n by design, and sometimes by a
 * smaller panel — a member can be scoped to the brand only when a provider's
 * daily quota will not stretch. `asymmetric` says so, and when it is true the
 * two consensuses were NOT built from the same models, which is a caveat the
 * table has to carry rather than a footnote nobody reads.
 */

import React from 'react';
import { Alert, Card, Space, Table, Typography } from 'antd';
import { THEME } from '../../config/theme';
import { getDisplayName } from '../../../../utils/elementDisplayNames';

const { Text, Paragraph } = Typography;

// The four that feed Brand Equity directly, so a difference in the roll-up can
// be traced to where it came from rather than left as one number.
const COMPARED = [
  'Perceived Quality',
  'Brand Loyalty',
  'Willingness to Pay a Premium',
  'Associations',
];

interface TargetResult {
  name: string;
  result: {
    overallBrandEquityScore?: number | null;
    latentElementScores?: { element: string; score: number | null }[];
  } | null;
}

interface Props {
  brandName: string;
  consensus: { result?: any } | null;
  competitors: TargetResult[];
  panelCoverage?: { brand?: string[]; competitor?: string[]; asymmetric?: boolean } | null;
}

const score = (value: number | null | undefined): string =>
  value === null || value === undefined ? '—' : value.toFixed(2);

export const CompetitorScores: React.FunctionComponent<Props> = ({
  brandName,
  consensus,
  competitors,
  panelCoverage,
}) => {
  if (!competitors?.length || !consensus?.result) return null;

  const rowFor = (name: string, result: any, isBrand: boolean) => {
    const byName = new Map(
      (result?.latentElementScores ?? []).map((e: any) => [e.element, e.score]),
    );
    return {
      key: name,
      name,
      isBrand,
      brandEquity: result?.overallBrandEquityScore ?? null,
      ...Object.fromEntries(COMPARED.map((el) => [el, byName.get(el) ?? null])),
    };
  };

  const rows = [
    rowFor(brandName, consensus.result, true),
    ...competitors.map((c) => rowFor(c.name, c.result, false)),
  ].sort((a, b) => (b.brandEquity ?? -101) - (a.brandEquity ?? -101));

  return (
    <Card title="Where you stand">
      <Space direction="vertical" size={12} style={{ width: '100%' }}>
        {panelCoverage?.asymmetric && (
          <Alert
            type="warning"
            showIcon
            message="These were not read by the same models"
            description={`Your brand was read by ${(panelCoverage.brand ?? []).join(', ')}; the competitors by ${(panelCoverage.competitor ?? []).join(', ')}. The comparison is still useful, but a gap between you and a competitor is partly a difference of instrument, not only of belief.`}
          />
        )}

        <Table
          size="small"
          pagination={false}
          dataSource={rows}
          columns={[
            {
              title: 'Brand',
              dataIndex: 'name',
              render: (name: string, row: any) => (
                <Text strong={row.isBrand}>{name}</Text>
              ),
            },
            {
              title: 'AI Brand Equity',
              dataIndex: 'brandEquity',
              width: 140,
              render: (value: number | null, row: any) => (
                <Text strong={row.isBrand}>{score(value)}</Text>
              ),
            },
            ...COMPARED.map((element) => ({
              title: getDisplayName(element),
              dataIndex: element,
              width: 150,
              render: (value: number | null) => score(value),
            })),
          ]}
        />

        <Paragraph style={{ color: THEME.textSecondary, marginBottom: 0, fontSize: 13, maxWidth: '72ch' }}>
          Competitors are read at half your sample size, so their intervals are wider than
          yours. Rank order is the reliable comparison here; a gap of a point or two is not.
        </Paragraph>
      </Space>
    </Card>
  );
};
